import { NotificationDeliveryTarget } from 'constants/notice';
import { PagePath } from 'constants/pagePath';
import { useNavigate } from 'react-router-dom';
import { useGlobalToastHandler } from 'views/mutual/globalToast/hooks';
import { useGetNotificationInfoQuery, useReadNotificationMutation } from 'views/utils/graphql/graphql';
import useOnceSubmit from 'views/utils/useOnceSubmit';
import { NoticeLocationState } from '../notice';

type NoticeDetailProps = {
  id: string;
  previousPage?: string; // HOMEとお知らせ画面のどちらに戻るかを持つ
};

export const useNoticeDetail = (props: NoticeDetailProps) => {
  const { showError } = useGlobalToastHandler();
  const navigate = useNavigate();

  const [notificationInfo, _reload] = useGetNotificationInfoQuery({
    variables: {
      id: props.id,
    },
    requestPolicy: 'network-only',
    pause: props.id ? false : true,
  });

  const [_readNoticeResponse, readNoticeInner] = useReadNotificationMutation();

  const canReadNotice =
    notificationInfo?.data?.GetNotificationInfo?.DeliveryTarget === NotificationDeliveryTarget.Personal &&
    notificationInfo?.data?.GetNotificationInfo?.Confirmed === false;

  // deliveryTargetを戻り先で維持するためにstateを渡す
  const navigatePreviousPage = () =>
    props.previousPage
      ? navigate(props.previousPage, {
          state: {
            deliveryTarget: notificationInfo.data?.GetNotificationInfo?.DeliveryTarget as NotificationDeliveryTarget,
          } as NoticeLocationState,
        })
      : navigate(PagePath.Notice, {
          state: {
            deliveryTarget: notificationInfo.data?.GetNotificationInfo?.DeliveryTarget as NotificationDeliveryTarget,
          } as NoticeLocationState,
        });

  const readNotice = useOnceSubmit<void>(
    {
      onPreviousSubmit: async () => {
        return canReadNotice;
      },
      onSubmit: async () => {
        const readNoticeData = await readNoticeInner({
          input: {
            ID: props.id,
          },
        });
        if (!readNoticeData?.data) return;

        if (readNoticeData.data.ReadNotification?.Code === 0) {
          navigatePreviousPage();
        } else if (readNoticeData.data) {
          showError(readNoticeData.data.ReadNotification?.Message);
        }
      },
    },
    [props.id, notificationInfo, canReadNotice, navigatePreviousPage],
  );

  return {
    notificationInfo,
    submitProps: canReadNotice
      ? {
          label: '確認',
          onClick: readNotice,
        }
      : {
          label: '閉じる',
          onClick: navigatePreviousPage,
        },
  };
};
